import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import axios from 'axios';

import accountsAPI from '@/api/accounts';
import organisationsAPI from '@/api/organisations';
import LayoutStandard from '@/components/LayoutStandard';
import SignUpForm, { SignUpFormValues } from '@/components/SignUpForm';
import MESSAGES from '@/constants/messages-en';
import { searchParamsQuery } from '@/helpers/urlHandler';
import useAuth from '@/hooks/useAuth';

function SignUpPage() {
  const { user, orgID, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const activationKey = location.state?.activationKey;
  const { invite, inviteEmail, type } = searchParamsQuery(
    window.location.search,
  );
  const searchParams = new URLSearchParams(location.search);
  const oauthError = searchParams.get('error');
  const inviteEmailRedacted = searchParams.get('invite_email_redacted');

  const [loadingLabel, setLoadingLabel] = useState<string | undefined>();
  const [apiError, setApiError] = useState<string | null>(null);
  const [emailFieldError, setEmailFieldError] = useState<string | undefined>();

  const inviteOAuthError = oauthError
    ? MESSAGES.auth.inviteOAuth.error[
        oauthError as keyof typeof MESSAGES.auth.inviteOAuth.error
      ] ?? MESSAGES.auth.inviteOAuth.error.oauth_failed
    : null;
  const displayedApiError =
    apiError ??
    (inviteOAuthError && inviteEmailRedacted
      ? `${inviteOAuthError}. Expected invited email: ${inviteEmailRedacted}`
      : inviteOAuthError);
  const loginLinkTo = invite ? `/login?invite=${encodeURIComponent(invite)}` : '/login';

  // Redirect if already authenticated
  useEffect(() => {
    if (user && orgID) {
      navigate('/', { state: { activationKey } });
    }
  }, [activationKey, orgID, user, navigate]);

  async function handleSubmit({
    firstName,
    lastName,
    email,
    orgName,
    password,
    confirmPassword,
  }: SignUpFormValues) {
    setApiError(null);
    setEmailFieldError(undefined);

    if (invite && inviteEmail) {
      // ── Invite flow ──────────────────────────────────────────────────────
      setLoadingLabel('Accepting invitation…');
      try {
        await organisationsAPI.acceptUserInvite({
          invite_code: invite,
          invite: {
            first_name: firstName,
            last_name: lastName,
            password,
            password_confirmation: confirmPassword,
          },
          type,
        });
        login({ email: inviteEmail, password });
      } catch {
        setApiError('Failed to accept invitation. Please try again.');
        setLoadingLabel(undefined);
      }
    } else {
      // ── Standard sign-up flow ────────────────────────────────────────────
      setLoadingLabel('Creating your account…');
      try {
        await accountsAPI.signup({
          email: email.trim().toLowerCase(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          organisation_name: orgName.trim(),
          password,
          password_confirmation: confirmPassword,
          terms_accepted: true,
        });
        setLoadingLabel('Signing you in…');
        login({ email: email.trim().toLowerCase(), password, activationKey });
      } catch (err) {
        setLoadingLabel(undefined);
        if (axios.isAxiosError(err)) {
          const emailErrors: string[] =
            err.response?.data?.errors?.email ?? [];
          if (
            emailErrors.some(
              (msg: string) =>
                msg.toLowerCase().includes('taken') ||
                msg.toLowerCase().includes('exist'),
            )
          ) {
            setEmailFieldError(
              'An account with this email already exists. Please log in instead.',
            );
            return;
          }
          setApiError(
            err.response?.data?.message ??
              err.response?.data?.error ??
              'Something went wrong. Please try again.',
          );
        } else {
          setApiError('Something went wrong. Please try again.');
        }
      }
    }
  }

  return (
    <LayoutStandard
      heading="Create your Tapt account"
      backgroundColor="bg-gray-50"
    >
      <SignUpForm
        initialEmail={invite && inviteEmail ? inviteEmail : ''}
        emailDisabled={!!(invite && inviteEmail)}
        showOrgField={!(invite && inviteEmail)}
        loadingLabel={loadingLabel}
        apiError={displayedApiError}
        fieldErrors={emailFieldError ? { email: emailFieldError } : undefined}
        onSubmit={handleSubmit}
        loginLink={{
          to: loginLinkTo,
          label: 'Sign in to Tapt',
          state: { activationKey },
        }}
        dividerText="Or create your account"
      />
    </LayoutStandard>
  );
}

export default SignUpPage;
